import { Body, Button, CloseIcon, HeadlineSize, Modal } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { StoreContext } from '../../store'
import { breakpoint } from '../../utils/breakpoint'
import { RemoveButton } from '../buttons/buttons'

interface IProps {
  businessId: string
  accountId: string
  userEmail: string
}

const RemoveUserModalComponent: React.FC<IProps> = ({ businessId, accountId, userEmail }) => {
  const [t] = useTranslation()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const { userDetailsStore, layoutStore } = useContext(StoreContext)

  const toggleModalOpen = useCallback(() => {
    setModalOpen((prevState) => !prevState)
  }, [])

  const handleDelete = useCallback(async () => {
    await userDetailsStore.removeOrganisation(businessId, accountId, userEmail)
    setModalOpen(false)
  }, [accountId, businessId, userDetailsStore, userEmail])

  const isFullscreen = !layoutStore.isDesktop

  return (
    <>
      <RemoveButton id={`remove_from_org_${businessId}_button`} onClick={toggleModalOpen} />
      {modalOpen &&
        createPortal(
          <ModalLayer>
            <Modal.Backdrop isOpen={modalOpen} onClick={toggleModalOpen} />
            <Modal isOpen={modalOpen} isFullscreen={isFullscreen} maxWidth="34.5rem">
              <Modal.Dialog role="dialog" style={{ minHeight: isFullscreen ? '100vh' : undefined }}>
                <Modal.CloseButton
                  type="button"
                  aria-label="Close"
                  onClick={toggleModalOpen}
                  contentMode="tertiary"
                  hasBackground
                  isOnlyIcon
                  leftIcon={CloseIcon}
                />
                <Modal.DialogHeadlineAligner>
                  <Modal.DialogHeadline size={HeadlineSize.Six}>{t(`removeModal.title`)}</Modal.DialogHeadline>
                </Modal.DialogHeadlineAligner>
                <Modal.DialogContent
                  style={{ flex: isFullscreen ? 1 : undefined, padding: '2rem', overflow: 'visible' }}
                >
                  <Modal.DialogHero>
                    <Body size="Three">{t(`removeModal.content`)}</Body>
                  </Modal.DialogHero>
                </Modal.DialogContent>
                <Modal.DialogButtons>
                  <ModalControls>
                    <Button
                      type="button"
                      size="sm"
                      contentMode="secondary"
                      hasBackground={false}
                      id="cancel_modal"
                      onClick={toggleModalOpen}
                    >
                      {t(`general.cancel`)}
                    </Button>
                    <Button type="button" size="sm" contentMode="primary" id="delete_modal" onClick={handleDelete}>
                      {t(`removeModal.removeButton`)}
                    </Button>
                  </ModalControls>
                </Modal.DialogButtons>
              </Modal.Dialog>
            </Modal>
          </ModalLayer>,
          document.getElementById('root')!
        )}
    </>
  )
}

export const RemoveUserModal = observer(RemoveUserModalComponent)

const ModalLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 110;
`

const ModalControls = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column-reverse;
    gap: ${theme.spacing.space2}rem;
    width: 100%;

    > *,
    button {
      width: 100%;
      justify-content: center;
    }

    @media ${breakpoint.lg} {
      flex-direction: row;
      justify-content: flex-end;

      > *,
      button {
        width: auto;
      }
    }
  `
)
